import Card from '../../components/Card';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';
import { useT } from '../../utils/useTranslation';
import api, { DB_TYPE } from '../../api';
import { db } from '../../firebase';
import { t as translate } from '../../utils/translations';
import { getDocs, collection, doc, updateDoc, addDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import Pagination from '../../components/Pagination';
import { motion, AnimatePresence } from 'framer-motion';

const ITEMS_PER_PAGE = 5;

const EMPTY = {
  title: '',
  titleSindhi: '',
  description: '',
  type: 'meeting', // Default to meeting as per roadmap
  date: '',
  time: '18:00',
  venue: '',
  isGlobal: false,
  branch: '',
  photo: '',
  attendees: [],
  performances: [] // { member_id, activity_type, details }
};

export default function ManageEvents() {
  const { user } = useAuth();
  const t = useT();
  const [items, setItems] = useState([]);
  const [members, setMembers] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [formMode, setFormMode] = useState('report');
  
  const isGlobalAdmin = user?.role === 'main_admin' || user?.role?.startsWith('center_body_');
  const isBranchAdmin = user?.role === 'branch_admin' || user?.role === 'branch_secretary_general';
  const canEditEvent = isGlobalAdmin || isBranchAdmin;

  const load = async () => {
    setLoading(true);
    if (DB_TYPE === 'firebase') {
      try {
        const q = query(collection(db, 'events'), orderBy('createdAt', 'desc'));
        const snap = await getDocs(q);
        let allItems = snap.docs.map(d => ({ _id: d.id, ...d.data() }));
        
        if (isGlobalAdmin) {
          // Main Admin only sees Meetings/Events (Reports), not Branch Announcements
          allItems = allItems.filter(item => item.type !== 'announcement');
        } else if (user?.branchId) {
          // Branch Admins/Members only see their own branch's activities
          allItems = allItems.filter(item => item.branch === user.branchId);
        }
        
        setItems(allItems);
        
        const memberSnap = await getDocs(collection(db, 'members'));
        setMembers(memberSnap.docs.map(d => ({ _id: d.id, ...d.data() })));
        
        const branchSnap = await getDocs(collection(db, 'branches'));
        setBranches(branchSnap.docs.map(d => ({ _id: d.id, ...d.data() })));
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    } else {
      api.get('/events').then(r => {
        setItems(r.data);
        setLoading(false);
      }).catch(() => setLoading(false));
    }
  };

  useEffect(() => {
    load();
  }, [user]);

  // Use canEditEvent calculated above

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const toggleAttendee = (memberId) => {
    const current = form.attendees || [];
    if (current.includes(memberId)) {
      set('attendees', current.filter(id => id !== memberId));
    } else {
      set('attendees', [...current, memberId]);
    }
  };

  const addPerformance = () => {
    set('performances', [...(form.performances || []), { member_id: '', activity_type: '', details: '' }]);
  };

  const updatePerformance = (idx, k, v) => {
    const next = [...(form.performances || [])];
    next[idx][k] = v;
    set('performances', next);
  };

  const removePerformance = (idx) => {
    set('performances', (form.performances || []).filter((_, i) => i !== idx));
  };

  const save = async e => {
    e.preventDefault();
    if (!canEditEvent) {
      toast.error('Unauthorized');
      return;
    }
    try {
      if (DB_TYPE === 'firebase') {
        if (editId) {
          await updateDoc(doc(db, 'events', editId), form);
          toast.success('Updated');
        } else {
          await addDoc(collection(db, 'events'), { ...form, createdAt: new Date().toISOString() });
          toast.success('Event/Meeting posted');
        }
      } else {
        if (editId) { await api.put(`/events/${editId}`, form); }
        else { await api.post('/events', form); }
        toast.success('Success');
      }
      setModal(false);
      load();
    } catch (err) {
      console.error(err);
      toast.error('Error');
    }
  };

  const del = async id => {
    if (!canEditEvent) {
      toast.error('Unauthorized');
      return;
    }
    const item = items.find(i => i._id === id);
    const today = new Date().toISOString().split('T')[0];
    if (item && item.date <= today && user?.role !== 'main_admin') { // Allow main admin for emergencies but user said "kesi sy bhi delete na ho"
        // Let's be strict as requested
        toast.error(isRTL ? 'توهان هن تقريب کي ڊاهي نٿا سگهو ڇو ته ان جي تاريخ اچي چڪي آهي.' : 'Cannot delete as the date has arrived.');
        return;
    }
    if (!window.confirm('Delete this event?')) return;
    try {
      if (DB_TYPE === 'firebase') { await deleteDoc(doc(db, 'events', id)); }
      else { await api.delete(`/events/${id}`); }
      toast.success('Deleted');
      load();
    } catch { toast.error('Error'); }
  };

  const isRTL = t.language === 'sindhi';

  const renderMemberName = (m) => isRTL ? (m.full_name_sindhi || m.full_name) : m.full_name;
  const renderBranchName = (b) => isRTL ? (b.nameSindhi || b.name) : b.name;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="fade-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 32, gap: 24, flexWrap: 'wrap', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
        <div>
          <h1 style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)', fontSize: isRTL ? 42 : 32, fontWeight: 800, color: 'var(--sindhi-navy)', marginBottom: 4 }}>
            {t('meetingsActivities')}
          </h1>
          <p style={{ fontSize: 14, color: 'var(--sindhi-text-muted)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
            {t('trackMeetingsDesc')}
          </p>
        </div>
        {canEditEvent && (
          <div style={{ display: 'flex', gap: 12, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
            {isBranchAdmin && (
              <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="btn btn-outline" style={{ height: 48, padding: '0 24px', borderRadius: 12, fontSize: 14, fontWeight: 800, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }} onClick={() => { setForm({ ...EMPTY, type: 'announcement', branch: user.branchId }); setFormMode('announcement'); setEditId(null); setModal(true); }}>
                {isRTL ? 'نئين تقريب / اعلان' : 'Log New Event / Announcement'}
              </motion.button>
            )}
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="btn btn-primary" style={{ height: 48, padding: '0 24px', borderRadius: 12, fontSize: 14, fontWeight: 800, background: 'var(--sindhi-maroon)', border: 'none', color: 'white', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }} onClick={() => { setForm({ ...EMPTY, type: 'meeting', branch: isBranchAdmin ? user.branchId : '' }); setFormMode('report'); setEditId(null); setModal(true); }}>
              {isRTL ? 'ايونٽ رپورٽ' : 'Event Report'}
            </motion.button>
          </div>
        )}
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 100 }}><div className="spinner" /></div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <AnimatePresence mode="popLayout">
            {items.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 80, background: 'white', borderRadius: 24, border: '1px solid var(--sindhi-cream)', direction: isRTL ? 'rtl' : 'ltr', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit', color: 'var(--sindhi-text-muted)' }}>{t('noRequestsFound')}</div>
            ) : (
              items.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE).map(item => {
                const displayTitle = isRTL ? (item.titleSindhi || item.title) : item.title;
                const branch = branches.find(b => b._id === item.branch);
                const displayBranch = branch ? renderBranchName(branch) : (item.branch || '—');

                return <Card key={item._id} style={{ padding: 24, display: 'flex', gap: 24, alignItems: 'center', direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left', background: 'white', border: '1px solid var(--sindhi-cream)' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexDirection: isRTL ? 'row-reverse' : 'row', justifyContent: 'flex-start', alignItems: 'center' }}>
                      <span style={{ padding: '6px 14px', borderRadius: 10, fontSize: 11, fontWeight: 800, background: 'var(--sindhi-ivory)', color: 'var(--sindhi-navy)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit', textTransform: 'uppercase' }}>
                        {isRTL ? (item.type === 'meeting' ? 'گڏجاڻي' : 'تقريب') : item.type}
                      </span>
                      <span style={{ color: 'var(--sindhi-maroon)', fontSize: 12, fontWeight: 700 }}>{item.date}</span>
                    </div>
                    <h3 style={{ fontSize: isRTL ? 28 : 20, fontWeight: 800, color: 'var(--sindhi-navy)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)', marginBottom: 12 }}>{displayTitle}</h3>
                    <div style={{ display: 'flex', gap: 20, fontSize: 14, color: 'var(--sindhi-text)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit', opacity: 0.8 }}>
                      <div><strong>{t('branch')}:</strong> {displayBranch}</div>
                      <div><strong>{t('members')}:</strong> {item.attendees?.length || 0}</div>
                    </div>
                  </div>
                    <div style={{ display: 'flex', gap: 8, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                      <Link to={`/events/${item._id}`} style={{ textDecoration: 'none' }}>
                        <button className="btn-action" style={{ background: 'var(--sindhi-navy)', color: 'white', border: 'none', padding: '10px 16px', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                          {isRTL ? 'تفصيل ڏسو' : 'View Details'}
                        </button>
                      </Link>
                      {(canEditEvent && (!isBranchAdmin || item.branch === (user.branchId || ''))) && (
                        <>
                          {(() => {
                            const eventTime = item.time || '00:00';
                            const eventDateTime = new Date(`${item.date}T${eventTime}`);
                            const now = new Date();
                            const isPastEditDeadline = (eventDateTime - now) < (60 * 60 * 1000);
                            const todayStr = now.toISOString().split('T')[0];
                            const isDateArrived = item.date <= todayStr;

                            return (
                              <>
                                {!isPastEditDeadline && (
                                  <button className="btn-action" style={{ background: 'var(--sindhi-ivory)', color: 'var(--sindhi-navy)', border: '1px solid var(--sindhi-cream)', padding: '10px 16px', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }} onClick={() => { setForm(item); setFormMode(item.type === 'announcement' ? 'announcement' : 'report'); setEditId(item._id); setModal(true); }}>{t('edit')}</button>
                                )}
                                {!isDateArrived && (
                                  <button className="btn-action" style={{ background: '#feecef', color: '#e11d48', border: 'none', padding: '10px 16px', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }} onClick={() => del(item._id)}>{t('delete')}</button>
                                )}
                              </>
                            );
                          })()}
                        </>
                      )}
                    </div>
                </Card>;
              })
            )}
          </AnimatePresence>
          <div style={{ display: 'flex', justifyContent: isRTL ? 'flex-start' : 'flex-end', padding: '0 10px' }}>
            <Pagination page={page} totalPages={Math.ceil(items.length / ITEMS_PER_PAGE)} onPageChange={setPage} />
          </div>
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {modal && (
          <div className="modal-overlay" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(10,25,47,0.4)', backdropFilter: 'blur(4px)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000 }} onClick={e => e.target === e.currentTarget && setModal(false)}>
            <motion.div initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9, y: 20 }} className="modal-box" style={{ width: 800, maxHeight: '90vh', overflowY: 'auto', borderRadius: 24, background: 'white', padding: 0, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
              <div style={{ padding: '24px 32px', background: 'var(--sindhi-ivory)', borderBottom: '1px solid var(--sindhi-cream)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                <h2 style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'var(--font-display)', fontSize: isRTL ? 32 : 24, fontWeight: 800, color: 'var(--sindhi-navy)' }}>{formMode === 'announcement' ? 'Log New Event / Announcement' : 'Event Report'}</h2>
                <button style={{ background: 'white', border: '1px solid var(--sindhi-cream)', width: 36, height: 36, borderRadius: 10, cursor: 'pointer' }} onClick={() => setModal(false)}>✕</button>
              </div>
              <form onSubmit={save} style={{ padding: 32 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                  <div className="form-group">
                    <label className="form-label">{translate('meetingTitleEnglish', 'english')} *</label>
                    <input className="form-input" value={form.title} onChange={e => set('title', e.target.value)} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label" style={{ fontFamily: 'var(--font-sindhi)' }}>{translate('meetingTitleSindhi', 'sindhi')} *</label>
                    <input className="form-input" style={{ fontFamily: 'var(--font-sindhi)', direction: 'rtl' }} value={form.titleSindhi} onChange={e => set('titleSindhi', e.target.value)} required />
                  </div>
                  {(!isBranchAdmin || !user?.branchId) && (
                    <div className="form-group">
                      <label className="form-label" style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('branches')} *</label>
                      <select className="form-input" value={form.branch} onChange={e => set('branch', e.target.value)} required style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                        <option value="">{isRTL ? 'شاخ چونڊيو' : (t('chooseBranch') || 'Select Branch')}</option>
                        {branches.map(b => <option key={b._id} value={b._id}>{renderBranchName(b)}</option>)}
                      </select>
                    </div>
                  )}
                  <div className="form-group">
                    <label className="form-label" style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('date')} *</label>
                    <input className="form-input" type="date" value={form.date} onChange={e => set('date', e.target.value)} required />
                  </div>
                  <div className="form-group">
                    <label className="form-label" style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{isRTL ? 'وقت' : 'Time'} *</label>
                    <input className="form-input" type="time" value={form.time} onChange={e => set('time', e.target.value)} required />
                  </div>
                  {formMode === 'report' && (
                    <div className="form-group">
                      <label className="form-label" style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{isRTL ? 'قسم' : t('type')} *</label>
                      <select className="form-input" value={form.type} onChange={e => set('type', e.target.value)} required style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>
                        <option value="meeting">{isRTL ? 'گڏجاڻي' : 'Meeting'}</option>
                        <option value="event">{isRTL ? 'تقريب' : 'Event'}</option>
                      </select>
                    </div>
                  )}
                  <div className="form-group">
                    <label className="form-label" style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{isRTL ? 'جڳهه' : t('venue')}</label>
                    <input className="form-input" value={form.venue || ''} onChange={e => set('venue', e.target.value)} style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }} />
                  </div>
                  
                  {formMode === 'report' && (
                    <>
                      <div className="form-group" style={{ gridColumn: '1/-1' }}>
                        <label className="form-label" style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('attendanceSelect')}</label>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12, maxHeight: 180, overflowY: 'auto', padding: 15, border: '1px solid var(--sindhi-cream)', borderRadius: 16, background: 'var(--sindhi-ivory)' }}>
                          {members.map(m => (
                            <button key={m._id} type="button" onClick={() => toggleAttendee(m._id)} style={{ padding: '8px 16px', borderRadius: 12, border: '1px solid var(--sindhi-cream)', background: form.attendees?.includes(m._id) ? 'var(--sindhi-maroon)' : 'white', color: form.attendees?.includes(m._id) ? 'white' : 'var(--sindhi-navy)', fontSize: 13, cursor: 'pointer', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit', fontWeight: 600, transition: 'all 0.2s' }}>
                              {renderMemberName(m)}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="form-group" style={{ gridColumn: '1/-1' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 15, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                          <label className="form-label" style={{ fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit', marginBottom: 0 }}>{t('memberPerformance')}</label>
                          <button type="button" onClick={addPerformance} style={{ background: 'var(--sindhi-navy)', color: 'white', border: 'none', padding: '8px 16px', borderRadius: 10, fontSize: 13, cursor: 'pointer', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit', fontWeight: 700 }}>+ {t('addActivity')}</button>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                          {(form.performances || []).map((p, idx) => (
                            <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 12, alignItems: 'end', background: 'var(--sindhi-ivory)', padding: 16, borderRadius: 16, direction: isRTL ? 'rtl' : 'ltr', border: '1px solid var(--sindhi-cream)' }}>
                              <div>
                                <label style={{ fontSize: 11, fontWeight: 800, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit', color: 'var(--sindhi-navy)', marginBottom: 6, display: 'block' }}>{t('members')}</label>
                                <select className="form-input" value={p.member_id} onChange={e => updatePerformance(idx, 'member_id', e.target.value)} style={{ height: 40, fontSize: 12, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit', background: 'white' }}>
                                  <option value="">{isRTL ? 'ميمبر چونڊيو' : t('chooseMember')}</option>
                                  {members.map(m => <option key={m._id} value={m._id}>{renderMemberName(m)}</option>)}
                                </select>
                              </div>
                              <div>
                                <label style={{ fontSize: 11, fontWeight: 800, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit', color: 'var(--sindhi-navy)', marginBottom: 6, display: 'block' }}>{isRTL ? 'سرگرمي جو قسم' : t('type')}</label>
                                <input className="form-input" value={p.activity_type} onChange={e => updatePerformance(idx, 'activity_type', e.target.value)} style={{ height: 40, fontSize: 12, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit', background: 'white' }} />
                              </div>
                              <div>
                                <label style={{ fontSize: 11, fontWeight: 800, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit', color: 'var(--sindhi-navy)', marginBottom: 6, display: 'block' }}>{isRTL ? 'تفصيل' : (t('descriptionLabel') || 'Details')}</label>
                                <input className="form-input" value={p.details} onChange={e => updatePerformance(idx, 'details', e.target.value)} style={{ height: 40, fontSize: 12, fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit', background: 'white' }} />
                              </div>
                              <button type="button" onClick={() => removePerformance(idx)} style={{ background: '#feecef', color: '#e11d48', border: 'none', width: 40, height: 40, borderRadius: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800 }}>✕</button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                <div style={{ marginTop: 32, display: 'flex', gap: 12, borderTop: '1px solid var(--sindhi-cream)', paddingTop: 24, flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                  <button type="submit" className="btn btn-primary" style={{ height: 50, flex: 1, borderRadius: 12, fontWeight: 800, background: 'var(--sindhi-maroon)', border: 'none', color: 'white', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('saveLog')}</button>
                  <button type="button" onClick={() => setModal(false)} className="btn btn-outline" style={{ height: 50, padding: '0 32px', borderRadius: 12, fontWeight: 800, border: '1px solid var(--sindhi-cream)', fontFamily: isRTL ? 'var(--font-sindhi)' : 'inherit' }}>{t('cancel')}</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}